import {
	Box,
	Card,
	CardHeader,
	Center,
	Flex,
	Heading,
	Text,
} from '@chakra-ui/react'
import { DDSTheme, ddsTokens } from '@ds/index'
import { useMemo } from 'react'

import { useGetHeatmapDataQuery } from '../api'
import { Granularity, TimeFrame } from '../types'
import { DAYS_OF_WEEK } from '../utils'
import { HOURS_OF_DAY } from '../utils'
import ChartSpinner from './ChartSpinner'
import { CustomHeatmapGrid } from './HeatmapGrid'

type MeetingsBookedHeatmapProps = {
	granularity: Granularity
	datePreset: TimeFrame
}

const MeetingsBookedHeatmap = ({
	granularity,
	datePreset,
}: MeetingsBookedHeatmapProps) => {
	const tokens = ddsTokens[DDSTheme.light]
	const { data: rawHeatmapData = [], isLoading } = useGetHeatmapDataQuery({
		datePreset,
		granularity,
	})

	const { processedGridData, maxValue } = useMemo(() => {
		const grid: number[][] = Array(7)
			.fill(null)
			.map(() => Array(24).fill(0))

		let maxVal = 0

		if (rawHeatmapData && rawHeatmapData.length > 0) {
			rawHeatmapData.forEach((item) => {
				const dayIndex = item.day_of_week - 1
				const hourIndex = item.meeting_hour

				if (dayIndex >= 0 && dayIndex < 7 && hourIndex >= 0 && hourIndex < 24) {
					grid[dayIndex][hourIndex] += item.meeting_count

					if (grid[dayIndex][hourIndex] > maxVal) {
						maxVal = grid[dayIndex][hourIndex]
					}
				}
			})
		}
		return { processedGridData: grid, maxValue: maxVal > 0 ? maxVal : 1 }
	}, [rawHeatmapData])

	const getBgColor = (value: number, currentMaxValue: number) => {
		if (currentMaxValue === 0) return tokens.primitive.light.gray[25]
		if (value === 0) return tokens.primitive.light.gray[25]

		// Create a scale from 100 to 600 using the intensity
		const intensity = currentMaxValue > 0 ? value / currentMaxValue : 0

		// Available color tokens are 100, 200, 300, 400, 500, 600
		// We want to map intensity (0 to 1) to these values
		if (intensity === 0) return tokens.primitive.light.primary[100]

		if (intensity < 0.2) return tokens.primitive.light.primary[100]
		if (intensity < 0.4) return tokens.primitive.light.primary[200]
		if (intensity < 0.6) return tokens.primitive.light.primary[300]
		if (intensity < 0.8) return tokens.primitive.light.primary[400]
		if (intensity < 0.95) return tokens.primitive.light.primary[500]
		return tokens.primitive.light.primary[600]
	}

	const hasData = rawHeatmapData && rawHeatmapData.length > 0

	return (
		<Card
			shadow="sm"
			p={4}
			border={`1px solid ${tokens.primitive.light.gray[100]}`}
		>
			<CardHeader p={0} mb={2}>
				<Flex justifyContent="space-between" alignItems="center">
					<Box>
						<Heading size="sm">Meeting Hotspots</Heading>
						<Text fontSize="sm" color="gray.500">
							Distribution of meetings booked by day of week and time of day
						</Text>
					</Box>
				</Flex>
			</CardHeader>
			<Box
				h="auto"
				w="100%"
				overflowX="auto"
				className="heatmap-container"
				position="relative"
			>
				{/* Always render a grid (even if empty) for consistent sizing during loading */}
				{hasData || isLoading ? (
					<Box position="relative">
						<CustomHeatmapGrid
							data={processedGridData}
							xLabels={HOURS_OF_DAY}
							yLabels={DAYS_OF_WEEK}
							maxValue={maxValue}
							getCellBackgroundColor={(val) => getBgColor(val, maxValue)}
						/>

						{isLoading && <ChartSpinner />}
					</Box>
				) : (
					<Center textAlign="center" p={10}>
						No data available for the selected period.
					</Center>
				)}

				{hasData && !isLoading && (
					<Flex
						justify="center"
						align="center"
						mt={2}
						fontSize="xs"
						color={tokens.primitive.light.gray[600]}
						position="sticky"
						bottom={0}
						left={0}
						right={0}
						py={2}
						bg="white"
						zIndex={1}
						borderTop={`1px solid ${tokens.primitive.light.gray[100]}`}
					>
						<Text mr={4}>Few</Text>

						<Box
							bg={getBgColor(0, maxValue)}
							w={3}
							h={3}
							borderRadius="sm"
							mr={1}
						/>

						<Box
							bg={getBgColor(Math.ceil(maxValue * 0.25), maxValue)}
							w={3}
							h={3}
							borderRadius="sm"
							mr={1}
						/>

						<Box
							bg={getBgColor(Math.ceil(maxValue * 0.5), maxValue)}
							w={3}
							h={3}
							borderRadius="sm"
							mr={1}
						/>

						<Box
							bg={getBgColor(maxValue, maxValue)}
							w={3}
							h={3}
							borderRadius="sm"
							mr={1}
						/>
						<Text>More</Text>
					</Flex>
				)}
			</Box>
		</Card>
	)
}

export default MeetingsBookedHeatmap
